import { forwardRef, InputHTMLAttributes } from 'react';
import Icon from '../AppIcon';

interface InputEmailProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  className?: string;
  label?: string;
  error?: string;
  helperText?: string;
  compact?: boolean;
}

const InputEmail = forwardRef<HTMLInputElement, InputEmailProps>(
  ({ className = '', label, error, helperText, compact = false, ...props }, ref) => {
    // Dynamic height and padding for hospitality apps
    const height = compact ? 'h-8' : 'h-9';
    const padding = compact ? 'pl-9 pr-3 py-1.5' : 'pl-10 pr-3 py-2';
    const iconSize = compact ? 14 : 16;
    const iconLeft = compact ? 'left-2.5' : 'left-3';

    const baseInputClass = `flex ${height} w-full rounded border bg-white ${padding} text-sm shadow-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 placeholder:text-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50`;

    const inputClass = error
      ? `${baseInputClass} border-red-500 focus:ring-red-500 focus:border-transparent`
      : `${baseInputClass} border-gray-300 focus:ring-[#1E3A5F] focus:border-transparent`;

    return (
      <div className="w-full">
        {label && <label className="block text-text-secondary text-xs mb-0.5">{label}</label>}
        <div className="relative">
          <div className={`absolute ${iconLeft} top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none`}>
            <Icon name="Mail" size={iconSize} />
          </div>
          <input type="email" className={`${inputClass} ${className}`} ref={ref} {...props} />
        </div>
        {error && (
          <p className="text-red-500 text-xs mt-0.5 flex items-center">
            <Icon name="AlertCircle" size={12} className="mr-1" />
            {error}
          </p>
        )}
        {helperText && !error && <p className="text-gray-500 text-xs mt-0.5">{helperText}</p>}
      </div>
    );
  }
);

InputEmail.displayName = 'InputEmail';

export default InputEmail;
